/**
 * @file 部落格版面配置
 * @description 前台部落格的版面配置，包含導覽列、Banner、側邊資訊卡、頁尾等元素
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { routerConfig, RouteConfig } from 'routes/router';
import routerPath from 'routes/router.path';
import api from 'services/api';

import useScrollToTop from 'hooks/useScrollToTop';

import Navbars from 'components/navbars/Navbars';
// import Avatar from 'components/avatar/Avatar';
// import Breadcrumb from 'components/breadcrumb/Breadcrumb';
// import IconButton from 'components/button/iconButton/IconButton';
// import Nav from 'components/nav/Nav';
// import ToolTip from 'components/tooltip/ToolTip';

import { Container, Row, Col } from 'react-bootstrap';
import './bloggerLayoutStyle.scss';
// import localStorageUtil from 'utils/localStorageUtil';
// import LocalStorageKeys from 'constants/localStorageKeys';

import { GetAsideCardDetailClass } from './getAsideCardDetailClass';

import ExceptionHandleService from 'utils/exceptionHandler';

const _EHS = new ExceptionHandleService({
  _NAME: 'layout/BloggerLayout.tsx',
  _NOTICE: ''
});

/** Nav */
const BloggerLayoutNav: React.FC = () => {
  const [isActive, setIsActive] = useState(false); // 用於管理 navbar 的狀態

  useEffect(() => {
    let lastScrollTop = 0;

    const handleScroll = (): void => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      // 根據滾動方向更新狀態
      if (scrollTop > lastScrollTop) {
        setIsActive(true); // 向下滾動時設為 active
      } else {
        setIsActive(false); // 向上滾動時移除 active
      }
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop; // 防止負值
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`blogger_layout_nav_menu` + (isActive ? ' isActive' : '')}>
      <Navbars
        brand='Gorgeous Wade'
        sideNavTitle='Gorgeous Wade'
        subPath={[
          {
            path: routerPath.posts,
            title: '文章',
            icon: ''
          },
          // {
          //   path: routerPath.timeline,
          //   title: '時間軸',
          //   icon: ''
          // },
          // {
          //   path: routerPath.categories,
          //   title: '分類',
          //   icon: ''
          // },
          {
            path: routerPath.about,
            title: '關於我',
            icon: ''
          }
        ]}
      />
    </nav>
  );
};

/** 部落格版面配置 Props */
interface BloggerLayoutProps {
  bannerHeight?: string;
}

/**
 * 部落格版面配置元件
 * @description 提供導覽列、Banner、側邊資訊卡、頁尾的完整版面結構
 */
const BloggerLayout: React.FC<BloggerLayoutProps> = ({ bannerHeight }) => {
  const location = useLocation();

  // 使用自定義 hook 來監聽路由變化並滾動到頂部
  useScrollToTop();

  // 遞迴獲取最深層次且最匹配當前路徑的路由配置 反向遍歷
  const getMatchedRoute = (
    routes: RouteConfig[],
    pathname: string,
    prefix: string = ''
  ): RouteConfig | null => {
    for (let i = routes.length - 1; i >= 0; i--) {
      const route = routes[i];
      const path = `${prefix}/${route.path}`.replace('//', '/');
      if (pathname.startsWith(path)) {
        if (route.children) {
          const matchedRoute = getMatchedRoute(route.children, pathname, path);
          if (matchedRoute) {
            return matchedRoute;
          }
        }
        return route;
      }
    }
    return null;
  };
  const currentRoute = getMatchedRoute(routerConfig[0]?.children || [], location.pathname);
  // console.log('currentRoute', currentRoute, currentRoute?.path?.length);
  return (
    <div id='blogger_layout'>
      {/* Side Menu */}
      <BloggerLayoutNav />
      <div
        className='blogger_layout_banner'
        style={{
          height: (currentRoute?.path?.length ?? 0) > 1 ? '50vh' : `${bannerHeight}vh`,
          backgroundImage: `url(${
            currentRoute?.bannerImg ??
            'https://storage.cloud.google.com/personal_public/blog/iceland.webp'
          })`
        }}
      >
        <div className='blogger_layout_banner_intro'>
          <h1>{(currentRoute?.path?.length ?? 0) > 1 ? '' : `I'm Wade Wu`}</h1>
        </div>
      </div>
      {/* Main content */}
      <Container className='container'>
        <Row className='blogger_layout_main'>
          {/* Aside Card */}
          {currentRoute?.hiddenAsideCard ? null : (
            <Col sm={0} md={3} className='blogger_layout_aside d-none d-md-block'>
              <BloggerLayoutAsideCard />
            </Col>
          )}
          {/* Content */}
          <Col
            sm='auto'
            md={currentRoute?.hiddenAsideCard ? 12 : 9}
            className='blogger_layout_content'
          >
            <Outlet />
          </Col>
        </Row>
      </Container>
      <BloggerLayoutFooter />
    </div>
  );
};

/** 側邊資訊小卡 */
const BloggerLayoutAsideCard: React.FC = () => {
  const navigate = useNavigate();
  const [postCount, setPostCount] = useState<number | null>(null);
  const [categoriesCount, setCategoriesCount] = useState<number | null>(null);

  /** 取得側邊欄小卡資訊 */
  const getAsideCardDetail = useCallback(async (): Promise<GetAsideCardDetailClass | undefined> => {
    try {
      const apiRes = await api.common.getAsideCardDetail();
      if (apiRes) {
        const res = new GetAsideCardDetailClass(apiRes);
        return res;
      }
    } catch (error) {
      _EHS.errorReport(error, 'getAsideCardDetail', _EHS._LEVEL.ERROR);
    }
  }, []);

  /** 初次載入 */
  const getInit = useCallback(async (): Promise<void> => {
    try {
      const asideCardInfo = await getAsideCardDetail();
      const postCount = asideCardInfo?.postCount;
      const categoriesCount = asideCardInfo?.categoriesCount;
      setPostCount(postCount ?? null);
      setCategoriesCount(categoriesCount ?? null);
    } catch (error) {
      _EHS.errorReport(error, 'getInit', _EHS._LEVEL.ERROR);
    }
  }, [getAsideCardDetail]);

  /** 初始化 */
  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className='blogger_layout_aside_card'>
      <section className='blogger_layout_aside_card_avatar'>
        <img
          alt='Spanner Wade'
          src='https://storage.cloud.google.com/personal_public/blog/post_images/common/avatar.jpg'
        ></img>
      </section>
      <section className='blogger_layout_aside_card_name'>
        <h2>Wade Wu</h2>
        <p>Gorgeous Wade</p>
      </section>
      <section className='blogger_layout_aside_card_counts'>
        <div onClick={() => navigate(routerPath.posts)}>
          <p>文章</p>
          <p>{postCount}</p>
        </div>
        <div onClick={() => navigate(routerPath.categories)}>
          <p>分類</p>
          <p>{categoriesCount}</p>
        </div>
      </section>
      <section className='blogger_layout_aside_card_social'></section>
    </div>
  );
};

/** Footer */
const BloggerLayoutFooter: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className='blogger_layout_footer'>
      <p>© {currentYear} By Wade Wu</p>
      <p>Theme From HEXO Butterfly</p>
    </footer>
  );
};

export default BloggerLayout;
